import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchRingSettings, fetchWaitingOrders } from './ring-api'
import { loudnessGain, playRing } from './ring-audio'
import {
  RingSettingsError, heardUntil, inQuietHours, markHeard, parseRingSettings, ringRows, soundsNow,
  type RingRow, type RingScreen,
} from './ring-rules'

// R503 (c): the till's poll loop, HOP's useRing (R503 (b)) ported with the same shape. Each poll
// reads GET /admin/settings, then GET /admin/orders/waiting, decides, plays the ring once when it
// should, and sets the next poll `ring.interval_s` later. The reads run from mount; the sound
// waits for a running AudioContext (the tap). When the till's final check moves an order on, the
// waiting read drops it and the next poll stops its ring.

// The retry pace after a failed poll that never read the settings; once a poll has read them, a
// failure retries at the owner's interval.
export const RING_RETRY_S = 10

export interface RingState {
  phase: 'idle' | 'checking' | 'ready' | 'failed'
  rows: RingRow[]
  on: boolean
  quiet: boolean
  pausedUntil: number | null
  sounding: boolean
}

const IDLE: RingState = { phase: 'idle', rows: [], on: true, quiet: false, pausedUntil: null, sounding: false }

export function useRing(screen: RingScreen, audio: AudioContext | null): RingState & { heard: () => void } {
  const [state, setState] = useState<RingState>(IDLE)
  const pauseRef = useRef<{ heardPauseS: number | null; memoryUntil: number | null }>({ heardPauseS: null, memoryUntil: null })
  const intervalRef = useRef<number | null>(null)

  const pausedAt = useCallback((nowMs: number): number | null => {
    const stored = heardUntil(nowMs)
    const memory = pauseRef.current.memoryUntil
    const fromMemory = memory !== null && memory > nowMs ? memory : null
    if (stored === null) return fromMemory
    return fromMemory === null ? stored : Math.max(stored, fromMemory)
  }, [])

  useEffect(() => {
    // Rook #67784 (2): a read needs no gesture, so the loop runs before the tap and the list shows;
    // only the sound waits for a running AudioContext. A tap restarts the loop with a fresh read.
    let alive = true
    let timer: number | undefined
    const tick = async () => {
      try {
        const settings = parseRingSettings(await fetchRingSettings())
        intervalRef.current = settings.intervalS
        pauseRef.current.heardPauseS = settings.heardPauseS
        const cfg = settings.screens[screen]
        if (!cfg) throw new RingSettingsError(`ring.screens.${screen} is missing`)
        const waiting = await fetchWaitingOrders()
        if (!alive) return
        const nowMs = Date.now()
        const rows = ringRows(waiting?.orders, cfg)
        const quiet = inQuietHours(new Date(nowMs), settings.quietHours, settings.timeZone)
        const pausedUntil = pausedAt(nowMs)
        const sounding = audio !== null && soundsNow({ rows, on: cfg.on, quiet, pausedUntil, loudness: settings.loudness })
        setState({ phase: 'ready', rows, on: cfg.on, quiet, pausedUntil, sounding })
        if (sounding && audio) playRing(audio, loudnessGain(settings.loudness))
      } catch {
        // A failed poll shows its face and rings for nothing: the list is dropped, so the ring
        // is never left on from an older answer.
        if (!alive) return
        setState({ ...IDLE, phase: 'failed' })
      }
      if (alive) timer = window.setTimeout(() => void tick(), (intervalRef.current ?? RING_RETRY_S) * 1000)
    }
    setState((s) => (s.phase === 'idle' ? { ...IDLE, phase: 'checking' } : s))
    void tick()
    return () => {
      alive = false
      if (timer !== undefined) window.clearTimeout(timer)
    }
  }, [audio, screen, pausedAt])

  const heard = useCallback(() => {
    const pauseS = pauseRef.current.heardPauseS
    if (pauseS === null) return
    const until = markHeard(Date.now(), pauseS)
    pauseRef.current.memoryUntil = until
    setState((s) => ({ ...s, pausedUntil: until, sounding: false }))
  }, [])

  return { ...state, heard }
}
